import React, {useState, useEffect, Fragment} from 'react';
import {Link} from 'react-router-dom';

import {inject, observer} from 'mobx-react';

import {Api, Validation} from '@wellstone-solutions/common';
import {
  DatePicker,
  Stack,
  Typography,
  Alert,
  Select,
  TextField,
} from '@wellstone-solutions/web';
import Button from '../../tutorial/button';
import {Tag} from 'react-bulma-components';
import {styleTag} from '../../../utils/Styler';
import {EventEmitter} from '../../../utils/EventEmitter';
import {
  fieldStatus,
  genders,
  identities,
  memberStructure,
} from '../../../utils/Forms';
import {Theme} from '@wellstone-solutions/common';

import moment from 'moment';
import {AccessControl, ENTITY_LEVEL, ENTITY_TYPES} from 'modules/rbac';
import {
  isValidDateString,
  isEndDateBeforeStartDate,
  hasInvalidAdmissionDates,
} from 'modules/member/utils';

const EditMemberForm = (props) => {
  const {member, meStore, RBACStore} = props;
  const {id, user} = member;

  const [name, setName] = useState(memberStructure.name.default);
  const [email, setEmail] = useState(user.email);
  const [gender, setGender] = useState(
    member?.gender || memberStructure.gender.default.value,
  );
  const [birthdate, setBirthdate] = useState(user.birthdate);
  const [identity, setIdentity] = useState(
    member?.identifies_as || memberStructure.identity.default.value,
  );
  const [programAdmissions, setProgramAdmissions] = useState(
    member.admissions.filter((adm) => !adm.discharge_date),
  );
  const [groupAdmissions, setGroupAdmissions] = useState(
    member.group_admissions.filter((adm) => !adm.discharge_date),
  );
  const [isProcessing, setIsProcessing] = useState(false);
  // allow birthdate to remain null only if it's currently null
  const isValidBirthday =
    (!user.birthdate && !birthdate) || Validation.isEligibleAge(birthdate);

  const updateProgramAdmissions = (index, property, val) => {
    const newPrograms = [...programAdmissions];
    newPrograms[index].updated = true;
    newPrograms[index][property] = val;
    setProgramAdmissions(newPrograms);
  };

  const updateGroupAdmissions = (index, property, val) => {
    const newGroups = [...groupAdmissions];
    groupAdmissions[index].updated = true;
    groupAdmissions[index][property] = val;
    setGroupAdmissions(newGroups);
  };

  const fields = {
    name: name,
    gender: gender,
    email: email,
    identity: identity,
  };

  useEffect(() => {
    setName(member.name);
  }, [member.name]);

  const _checkForErrors = () => {
    let hasErrors = false;
    for (let [key, value] of Object.entries(memberStructure)) {
      if (fieldStatus(fields[key], value.test, value.required).error) {
        hasErrors = true;
      }
    }

    if ((user.birthdate && !birthdate) || !isValidBirthday) {
      hasErrors = true;
    }

    if (hasInvalidAdmissionDates(programAdmissions, groupAdmissions)) {
      hasErrors = true;
    }

    return hasErrors;
  };

  const _updateMember = async () => {
    const org = meStore.me.membership.organization.id;

    let params = {
      role: 'patient',
      gender: gender.toLowerCase(),
      identifies_as: identity.toLowerCase(),
      name: name,
      email: email,
    };

    if (birthdate) {
      params.birthdate = birthdate;
    }

    try {
      setIsProcessing(true);
      const promises = [
        Api.Instance.current().post('/orgs/' + org + '/members/' + id, params),
      ];

      // If email has changed, update it
      if (email && email !== user.email && user.id) {
        promises.push(
          Api.Instance.current().put(`/users/${user.id}`, {email: email}),
        );
      }

      programAdmissions.forEach((admission) => {
        if (admission.updated) {
          // Explicitly remove fields that should not be in the payload
          /* eslint-disable no-unused-vars */
          const {
            updated,
            program,
            discharge_date,
            member,
            organization,
            created,
            modified,
            ...payload
          } = admission;
          /* eslint-enable no-unused-vars */

          promises.push(
            Api.Instance.current().post(
              `/orgs/${org}/admissions/${admission.id}`,
              payload,
            ),
          );
        }
      });

      groupAdmissions.forEach((admission) => {
        if (admission.updated) {
          const payload = {
            start_date: admission.start_date,
            end_date: admission.end_date,
          };
          promises.push(
            Api.Instance.current().patch(
              `/orgs/${org}/groups/${admission.group.id}/admissions/${admission.id}`,
              payload,
            ),
          );
        }
      });

      Promise.all(promises)
        .then((responses) => {
          setIsProcessing(false);
          EventEmitter.dispatch('closeModal', responses[0]);
        })
        .catch((error) => {
          setIsProcessing(false);
        });
    } catch (error) {
      console.error('Failed to update member:', error);
      setIsProcessing(false);
    }
  };

  const genderItems = genders.map(({name, value}) => ({label: name, value}));
  const identityItems = identities.map(({name, value}) => ({
    label: name,
    value,
  }));

  return (
    <div className={'edit-member-form'}>
      <Stack
        direction={'column'}
        justifyContent="space-between"
        alignItems={'flex-start'}
        spacing={2}
        sx={{mb: 2, mt: 2}}>
        <TextField
          fullWidth
          label="Full Name*"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={
            fieldStatus(
              name,
              memberStructure.name.test,
              memberStructure.name.required,
            ).error
          }
          helperText={
            fieldStatus(
              name,
              memberStructure.name.test,
              memberStructure.name.required,
            ).error
              ? 'This field is required'
              : ''
          }
          margin="normal"
          variant="outlined"
          size="small"
        />
      </Stack>

      <TextField
        fullWidth
        label="Email*"
        placeholder="Email"
        value={email}
        disabled={!RBACStore.hasAccess(AccessControl.members.editMemberEmail)}
        onChange={(e) => setEmail(e.target.value)}
        error={
          fieldStatus(
            email,
            memberStructure.email.test,
            memberStructure.email.required,
          ).error
        }
        helperText={
          fieldStatus(
            email,
            memberStructure.email.test,
            memberStructure.email.required,
          ).error
            ? fieldStatus(
                email,
                memberStructure.email.test,
                memberStructure.email.required,
              ).error === 'Field is Required'
              ? 'This field is required'
              : 'Invalid Email'
            : ''
        }
        margin="normal"
        variant="outlined"
        size="small"
      />

      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{mb: 1}}>
        <Typography variant="body1">Birthdate*</Typography>
        <DatePicker
          InputProps={{
            sx: {
              border: isValidBirthday
                ? '1px solid #43b017'
                : '1px solid #f68e07',
              borderRadius: 1,
              '.MuiOutlinedInput-input': {
                py: '8px',
                px: '11px',
              },
              '.MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
                {
                  border: 'none',
                },
              fullWidth: true,
              mb: 2,
            },
          }}
          fieldName="birthdate"
          onChange={(date) => {
            setBirthdate(date?.isValid() ? date.format('YYYY-MM-DD') : '');
          }}
          value={birthdate}
        />
        {!isValidBirthday && (
          <Typography color="error" sx={{textAlign: 'right', mb: 1, mt: -1}}>
            Member must be 13 and over
          </Typography>
        )}
      </Stack>

      <Select
        label="Sex"
        value={gender}
        items={genderItems}
        onChange={(e) => setGender(e.target.value)}
        canUnselect={false}
        containerProps={{
          size: 'small',
          sx: {width: '100%', mb: 2},
        }}
      />

      <Select
        label="Identifies As"
        value={identity}
        items={identityItems}
        onChange={(e) => setIdentity(e.target.value)}
        canUnselect={false}
        containerProps={{
          size: 'small',
          sx: {width: '100%'},
        }}
      />

      <Fragment>
        <hr />
        <Typography variant="h6">Groups</Typography>
        {groupAdmissions && groupAdmissions.length > 0 ? (
          <ul className="inline-group-list">
            {groupAdmissions.map((admission, index) => {
              const isPermitted =
                RBACStore.hasEntityLevelAccess(ENTITY_LEVEL.all) ||
                RBACStore.hasAccessToEntity(
                  ENTITY_TYPES.group,
                  admission.group.id,
                );
              return (
                <li key={index}>
                  <div className={'tag-wrapper'}>
                    <Link
                      key={index}
                      to={`/groups/${admission.group.id}`}
                      className={'tag group-tag is-info'}
                      style={{
                        ...styleTag(
                          props.organizationStore.getGroupCategoryById(
                            admission.group?.category_id,
                          ).name,
                          true,
                        ),
                      }}>
                      {admission.group.name}
                    </Link>
                  </div>
                  <div className={'inline'}>
                    <span>
                      {admission.start_date === null ? (
                        'N/A'
                      ) : (
                        <DatePicker
                          value={admission.start_date || null}
                          disabled={!isPermitted}
                          error={
                            !isValidDateString(admission.start_date) ||
                            isEndDateBeforeStartDate(
                              admission.start_date,
                              admission.end_date,
                            )
                          }
                          onChange={(val) =>
                            updateGroupAdmissions(
                              index,
                              'start_date',
                              val?.isValid() ? val.format('YYYY-MM-DD') : '',
                            )
                          }
                        />
                      )}
                    </span>
                    <span className={'middle'}>- to -</span>
                    <span>
                      {admission.end_date != null ? (
                        <DatePicker
                          value={admission.end_date || null}
                          disabled={!isPermitted}
                          error={
                            !isValidDateString(admission.end_date) ||
                            isEndDateBeforeStartDate(
                              admission.start_date,
                              admission.end_date,
                            )
                          }
                          onChange={(val) =>
                            updateGroupAdmissions(
                              index,
                              'end_date',
                              val?.isValid() ? val.format('YYYY-MM-DD') : '',
                            )
                          }
                          minDate={groupAdmissions[index]?.start_date}
                        />
                      ) : (
                        <Button
                          color="primary"
                          onClick={() =>
                            updateGroupAdmissions(
                              index,
                              'end_date',
                              new moment().format('YYYY-MM-DD'),
                            )
                          }>
                          Add
                        </Button>
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <Alert severity="info">
            Member currently not assigned to any groups
          </Alert>
        )}
        <hr />
        <Typography variant="h6">Programs</Typography>
        {member.admissions?.length ? (
          <>
            <ul className="inline-group-list">
              {programAdmissions.map((admission, index) => {
                const isPermitted =
                  RBACStore.hasEntityLevelAccess(ENTITY_LEVEL.all) ||
                  RBACStore.hasAccessToEntity(
                    ENTITY_TYPES.program,
                    admission.program.id,
                  );
                return (
                  <li key={index}>
                    <div className={'tag-wrapper'}>
                      <Tag
                        key={index}
                        color="info"
                        className={'group-tag'}
                        style={{
                          ...styleTag(admission.program.name),
                        }}>
                        {admission.program.name}
                      </Tag>
                    </div>
                    <div className={'inline'}>
                      <span>
                        {admission.start_date === null ? (
                          'N/A'
                        ) : (
                          <DatePicker
                            value={admission.start_date || null}
                            disabled={!isPermitted}
                            error={
                              !isValidDateString(admission.start_date) ||
                              isEndDateBeforeStartDate(
                                admission.start_date,
                                admission.end_date,
                              )
                            }
                            onChange={(val) =>
                              updateProgramAdmissions(
                                index,
                                'start_date',
                                val?.isValid() ? val.format('YYYY-MM-DD') : '',
                              )
                            }
                          />
                        )}
                      </span>
                      <span className={'middle'}>- to -</span>
                      <span>
                        {admission.end_date === null ? (
                          'N/A'
                        ) : (
                          <DatePicker
                            value={admission.end_date || null}
                            disabled={!isPermitted}
                            error={
                              !isValidDateString(admission.end_date) ||
                              isEndDateBeforeStartDate(
                                admission.start_date,
                                admission.end_date,
                              )
                            }
                            onChange={(val) =>
                              updateProgramAdmissions(
                                index,
                                'end_date',
                                val.format('YYYY-MM-DD'),
                              )
                            }
                            minDate={programAdmissions[index]?.start_date}
                          />
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <Alert severity="info">
            Member currently not enrolled in a program
          </Alert>
        )}
      </Fragment>
      <hr />
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{width: '100%', mt: 2}}>
        <Button
          style={{border: 'none', color: Theme.colorPalette.primary}}
          onClick={() => EventEmitter.dispatch('closeModal', null)}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={_checkForErrors() ? true : false}
          isprocessing={isProcessing}
          onClick={_updateMember}>
          Save
        </Button>
      </Stack>
    </div>
  );
};

export default inject(
  'meStore',
  'organizationStore',
  'RBACStore',
)(observer(EditMemberForm));
